// Module: remote-storage
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §5, §6
//
// Client side of the Worker API. No ceremony, no key derivation, no browser storage: the
// checkpoint store is injected because its real implementation is IndexedDB and only `app` may
// touch that (spec §9.3).

import { pack, unpack, readHeader } from "../vault-container_2026-08-27/vault-container.js"

export type Session = { token: string; expiresAt: number }

export type Credential =
  | {
      kind: "assertion"
      /** The vault being opened. The Worker needs it to look the credential up (spec §5.4). */
      vaultId: string
      credentialId: Uint8Array
      authenticatorData: Uint8Array
      clientDataJSON: Uint8Array
      signature: Uint8Array
    }
  | {
      kind: "ed25519"
      vaultId: string
      /** The challenge that was signed. Ed25519 signs it directly, so it is not recoverable from
       *  a clientDataJSON the way an assertion's is. Used by the recovery code and by an adopted
       *  machine, which opens the vault without any WebAuthn ceremony. */
      challenge: string
      signature: Uint8Array
      publicKey: Uint8Array
    }

export type CheckpointStore = {
  get(vaultId: string): Promise<bigint | null>
  set(vaultId: string, revision: bigint): Promise<void>
}

export type RemoteStorageErrorCode =
  | "Unauthorized"
  | "Forbidden"
  | "NotFound"
  | "PreconditionFailed"
  | "PayloadTooLarge"
  | "RateLimited"
  | "ServerError"
  | "NetworkError"
  | "RollbackDetected"
  | "ObjectImmutable"
  | "MalformedResponse"

export class RemoteStorageError extends Error {
  readonly code: RemoteStorageErrorCode
  readonly status: number | undefined
  constructor(code: RemoteStorageErrorCode, message?: string, options?: { cause?: unknown; status?: number }) {
    super(message ?? code, options)
    this.name = "RemoteStorageError"
    this.code = code
    this.status = options?.status
  }
}

export const MAX_RECORD_BYTES = 64 * 1024

const b64u = (bytes: Uint8Array): string => {
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/u, "")
}

function statusToCode(status: number): RemoteStorageErrorCode {
  switch (status) {
    case 401:
      return "Unauthorized"
    case 403:
      return "Forbidden"
    case 404:
      return "NotFound"
    case 409:
      // The Worker refuses to overwrite a vault object. Reaching this means a caller tried to
      // reuse an objectId instead of minting a new one — a bug, not a race.
      return "ObjectImmutable"
    case 412:
      return "PreconditionFailed"
    case 413:
      return "PayloadTooLarge"
    case 429:
      return "RateLimited"
    default:
      return status >= 500 ? "ServerError" : "MalformedResponse"
  }
}

export interface RemoteStorage {
  getChallenge(): Promise<Uint8Array>
  authenticate(credential: Credential): Promise<Session>
  getRecord(vaultId: string): Promise<{ bytes: Uint8Array; etag: string }>
  putRecord(opts: {
    vaultId: string
    bytes: Uint8Array
    session: Session
    ifMatch: string | null
  }): Promise<{ etag: string }>
  getVault(opts: {
    vaultId: string
    objectId: string
    session: Session
    key: CryptoKey
  }): Promise<{ plaintext: Uint8Array; revision: bigint; etag: string }>
  /**
   * Always a create. Vault objects are immutable (spec §6.4): every save mints a fresh objectId
   * and the bootstrap record is then repointed at it, so a corrupt write lands at an address
   * nothing references yet and the previous revision survives untouched.
   */
  putVault(opts: {
    vaultId: string
    objectId: string
    session: Session
    key: CryptoKey
    plaintext: Uint8Array
    revision: bigint
  }): Promise<{ etag: string }>
  /**
   * Records that a revision is durably current. Called only after the bootstrap record has been
   * repointed, because that — not the object write — is the commit point (spec §6.4).
   */
  commitRevision(vaultId: string, revision: bigint): Promise<void>
  registerCredential(opts: {
    vaultId: string
    credentialId: Uint8Array
    publicKey: Uint8Array
    session: Session | null
    /** Recovery keys verify by a different route (Ed25519 over the challenge), so the Worker has
     *  to know which kind it is storing. Defaults to "assertion". */
    kind?: "assertion" | "ed25519"
  }): Promise<void>
}

export function createRemoteStorage(config: {
  baseUrl: string
  checkpoints: CheckpointStore
  fetch?: typeof globalThis.fetch
  clock?: () => number
}): RemoteStorage {
  const doFetch = config.fetch ?? globalThis.fetch.bind(globalThis)
  const now = config.clock ?? (() => Date.now())
  const base = config.baseUrl.replace(/\/+$/u, "")

  async function send(
    path: string,
    init: RequestInit & { session?: Session | null },
  ): Promise<Response> {
    const headers = new Headers(init.headers)

    if (init.session) {
      // Fail before the network rather than burning a round trip on a token we know is dead.
      // Renewal is `session`'s job: it is the only module that can prompt for a fresh ceremony.
      if (now() >= init.session.expiresAt) {
        throw new RemoteStorageError("Unauthorized", "session has expired")
      }
      headers.set("Authorization", `Bearer ${init.session.token}`)
    }

    let response: Response
    try {
      response = await doFetch(`${base}${path}`, { ...init, headers })
    } catch (cause) {
      throw new RemoteStorageError("NetworkError", `request to ${path} failed`, { cause })
    }

    if (!response.ok) {
      throw new RemoteStorageError(
        statusToCode(response.status),
        `${path} returned ${response.status}`,
        { status: response.status },
      )
    }
    return response
  }

  /** `If-Match` for an update, `If-None-Match: *` for a create. Never unconditional: an
   *  unconditional write is exactly the silent overwrite spec §6.2 exists to prevent. */
  function conditional(ifMatch: string | null): Record<string, string> {
    return ifMatch === null ? { "If-None-Match": "*" } : { "If-Match": ifMatch }
  }

  function etagOf(response: Response, path: string): string {
    const etag = response.headers.get("ETag")
    if (!etag) throw new RemoteStorageError("MalformedResponse", `${path} returned no ETag`)
    return etag
  }

  return {
    async getChallenge(): Promise<Uint8Array> {
      const response = await send("/api/challenge", { method: "GET" })
      let body: unknown
      try {
        body = await response.json()
      } catch (cause) {
        throw new RemoteStorageError("MalformedResponse", "challenge was not JSON", { cause })
      }
      const challenge = (body as { challenge?: unknown }).challenge
      if (typeof challenge !== "string" || challenge.length === 0) {
        throw new RemoteStorageError("MalformedResponse", "challenge missing from response")
      }
      const binary = atob(challenge.replace(/-/gu, "+").replace(/_/gu, "/"))
      const out = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i)
      return out
    },

    async authenticate(credential: Credential): Promise<Session> {
      const body =
        credential.kind === "assertion"
          ? {
              kind: "assertion",
              vaultId: credential.vaultId,
              credentialId: b64u(credential.credentialId),
              authenticatorData: b64u(credential.authenticatorData),
              clientDataJSON: b64u(credential.clientDataJSON),
              signature: b64u(credential.signature),
            }
          : {
              kind: "ed25519",
              vaultId: credential.vaultId,
              challenge: credential.challenge,
              signature: b64u(credential.signature),
              publicKey: b64u(credential.publicKey),
            }

      const response = await send("/api/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })

      let parsed: unknown
      try {
        parsed = await response.json()
      } catch (cause) {
        throw new RemoteStorageError("MalformedResponse", "session was not JSON", { cause })
      }
      const { token, expiresAt } = parsed as { token?: unknown; expiresAt?: unknown }
      if (typeof token !== "string" || typeof expiresAt !== "number") {
        throw new RemoteStorageError("MalformedResponse", "session response is missing fields")
      }
      return { token, expiresAt }
    },

    async getRecord(vaultId) {
      // Public by design: required to break v1.0's circularity (spec §2.5).
      const path = `/api/bootstrap/${vaultId}`
      const response = await send(path, { method: "GET" })
      const bytes = new Uint8Array(await response.arrayBuffer())
      if (bytes.length > MAX_RECORD_BYTES) {
        throw new RemoteStorageError("MalformedResponse", "bootstrap record is oversized")
      }
      return { bytes, etag: etagOf(response, path) }
    },

    async putRecord({ vaultId, bytes, session, ifMatch }) {
      const path = `/api/bootstrap/${vaultId}`
      const response = await send(path, {
        method: "PUT",
        session,
        headers: { "Content-Type": "application/json", ...conditional(ifMatch) },
        body: bytes as unknown as BodyInit,
      })
      return { etag: etagOf(response, path) }
    },

    async getVault({ vaultId, objectId, session, key }) {
      const path = `/api/vault/${objectId}`
      const response = await send(path, { method: "GET", session })
      const container = new Uint8Array(await response.arrayBuffer())
      const checkpoint = await config.checkpoints.get(vaultId)

      // Step 1: unauthenticated header. May only REJECT — never accept — and saves decrypting up
      // to 16 MB of a container already known to be stale.
      if (checkpoint !== null && readHeader(container).revision < checkpoint) {
        throw new RemoteStorageError(
          "RollbackDetected",
          `served revision is below the last seen ${checkpoint}`,
        )
      }

      // Step 2: authenticated header, via the AAD binding.
      const { plaintext, header } = await unpack({ container, key })

      // Step 3: the decision that counts.
      if (checkpoint !== null && header.revision < checkpoint) {
        throw new RemoteStorageError(
          "RollbackDetected",
          `authenticated revision ${header.revision} is below the last seen ${checkpoint}`,
        )
      }

      await config.checkpoints.set(vaultId, header.revision)
      return { plaintext, revision: header.revision, etag: etagOf(response, path) }
    },

    async putVault({ vaultId, objectId, session, key, plaintext, revision }) {
      const container = await pack({ plaintext, key, revision })
      const path = `/api/vault/${objectId}`
      const response = await send(path, {
        method: "PUT",
        session,
        // Create-only, never an overwrite. The Worker enforces this too, so immutability is a
        // server property rather than a client convention.
        headers: { "Content-Type": "application/octet-stream", ...conditional(null) },
        body: container as unknown as BodyInit,
      })
      // Deliberately does NOT advance the checkpoint. An object that is never referenced by the
      // record is not a revision of anything, and advancing here would wedge the device: the
      // checkpoint would sit above what the record points at, and every later unlock would report
      // a rollback that never happened.
      return { etag: etagOf(response, path) }
    },

    async commitRevision(vaultId, revision) {
      await config.checkpoints.set(vaultId, revision)
    },

    async registerCredential({ vaultId, credentialId, publicKey, session, kind }) {
      await send(`/api/credentials/${vaultId}`, {
        method: "POST",
        session,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          credentialId: b64u(credentialId),
          publicKey: b64u(publicKey),
          kind: kind ?? "assertion",
        }),
      })
    },
  }
}
